import { HOT_RANKING, HOT_TOPICS } from '@/mocks/entertainment';
import SectionHeading from './SectionHeading';

interface HotBoardProps {
  onTopic: (label: string) => void;
}

const TYPE_COLORS: Record<string, string> = {
  演员: 'bg-primary-100 text-primary-700',
  歌手: 'bg-accent-100 text-accent-700',
  爱豆: 'bg-secondary-100 text-secondary-900',
};

export default function HotBoard({ onTopic }: HotBoardProps) {
  return (
    <section id="hot" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8">
        <SectionHeading
          eyebrow="今日热榜"
          title="谁在风口浪尖 · 热度实时榜"
          desc="按最近 24 小时全网公开讨论热度动态排序，上榜理由实时更新，帮你快速判断今天该吃哪口瓜。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* ranking list */}
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-3xl border border-background-200/70 bg-background-50">
              <ol className="divide-y divide-background-100">
                {HOT_RANKING.map((it) => (
                  <li key={it.rank} className="flex items-center gap-4 px-4 py-4 md:px-6">
                    <span
                      className={`w-7 shrink-0 text-center font-heading text-xl font-extrabold ${
                        it.rank === 1
                          ? 'text-primary-500'
                          : it.rank === 2
                            ? 'text-secondary-500'
                            : it.rank === 3
                              ? 'text-accent-600'
                              : 'text-foreground-300'
                      }`}
                    >
                      {it.rank}
                    </span>
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-base font-bold text-background-50 ${
                        it.rank === 1
                          ? 'bg-primary-500'
                          : it.rank === 2
                            ? 'bg-secondary-500'
                            : it.rank === 3
                              ? 'bg-accent-500'
                              : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {it.name.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading text-base font-bold text-foreground-950">
                          {it.name}
                        </h3>
                        <span
                          className={`rounded-full px-2 py-0.5 font-label text-[11px] font-medium ${
                            TYPE_COLORS[it.type] || 'bg-background-100 text-foreground-600'
                          }`}
                        >
                          {it.type}
                        </span>
                        {it.trend === 'new' && (
                          <span className="inline-flex items-center gap-0.5 font-label text-[11px] font-semibold text-primary-600">
                            <i className="ri-sparkling-line"></i>新上榜
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-sm text-foreground-600">{it.reason}</p>
                    </div>
                    <div className="hidden shrink-0 flex-col items-end sm:flex">
                      <span className="inline-flex items-center gap-1 font-heading text-lg font-bold text-foreground-950">
                        {it.heat}
                      </span>
                      <span className="font-label text-[11px] text-foreground-400">热度指数</span>
                    </div>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                      {it.trend === 'up' && (
                        <i className="ri-arrow-up-line text-base font-bold text-accent-600"></i>
                      )}
                      {it.trend === 'down' && (
                        <i className="ri-arrow-down-line text-base font-bold text-primary-500"></i>
                      )}
                      {it.trend === 'same' && (
                        <i className="ri-arrow-right-line text-base text-foreground-300"></i>
                      )}
                      {it.trend === 'new' && (
                        <i className="ri-arrow-up-line text-base font-bold text-accent-600"></i>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* side panel */}
          <div className="flex flex-col gap-6">
            <div className="rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 p-6 md:p-7 text-background-50">
              <p className="font-label text-xs tracking-widest text-background-100/80">
                话题风向标
              </p>
              <h3 className="mt-2 font-heading text-xl font-bold">今天大家聊什么</h3>
              <div className="mt-4 flex flex-col gap-2.5">
                {HOT_TOPICS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onTopic(t.label)}
                    className="flex items-center justify-between rounded-xl bg-background-50/10 px-3.5 py-2.5 text-left transition-colors hover:bg-background-50/20"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-background-50">
                      <span className="h-1.5 w-1.5 rounded-full bg-secondary-300"></span>
                      {t.label}
                    </span>
                    <span className="font-label text-[11px] text-background-100/80">{t.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-1 flex-col rounded-3xl border border-background-200/70 bg-background-100/60 p-6 md:p-7">
              <p className="font-label text-xs tracking-widest text-foreground-400">每日报告</p>
              <div className="mt-3 flex items-end gap-1.5" aria-hidden="true">
                {[42, 68, 55, 84, 63, 95, 78].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-md bg-gradient-to-t from-primary-400 to-secondary-400"
                    style={{ height: `${h}%` }}
                  ></div>
                ))}
              </div>
              <div className="mt-4 space-y-2 text-sm text-foreground-600">
                <p className="flex items-center justify-between">
                  <span>今日新增热瓜</span>
                  <strong className="text-foreground-900">86 条</strong>
                </p>
                <p className="flex items-center justify-between">
                  <span>正在上升话题</span>
                  <strong className="text-primary-600">12 个</strong>
                </p>
                <p className="flex items-center justify-between">
                  <span>已核验信源</span>
                  <strong className="text-accent-600">41 条</strong>
                </p>
              </div>
              <a
                href="#feed"
                className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                看全部热瓜
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}